import { NxActionModule } from '@allianz/ng-aquila/action';
import { NxIconModule } from '@allianz/ng-aquila/icon';
import { NxFlatTreeControl, NxFlatTreeNode, NxTreeFlatDataSource, NxTreeModule, NxTreeNode } from '@allianz/ng-aquila/tree';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, map, tap } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { MenuItem } from '../../../../shared/interfaces/globtroter.interface';
import { ApiService } from '../../../../shared/services/api.service';
import { staticGlobtroterPaths } from '../static-globtroter-paths';

export interface MyTreeNode extends NxTreeNode {
  children?: MyTreeNode[];
  label: string;
  link?: string;
  isSpaLink?: boolean;
}

interface MyFlatTreeNode extends NxFlatTreeNode {
  label: string;
  link?: string;
  isSpaLink?: boolean;
}

@Component({
  standalone: true,
  imports: [NxActionModule, NxIconModule, NxTreeModule],
  selector: 'app-mobile-menu',
  templateUrl: './mobile-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileMenuComponent {
  private apiService = inject(ApiService);
  private translateService = inject(TranslateService);

  readonly staticGlobtroterPaths = staticGlobtroterPaths;
  globtroterApp = environment.globtroterApp;
  treeControl = new NxFlatTreeControl<MyFlatTreeNode>();
  dataSource = new NxTreeFlatDataSource<MyTreeNode, MyFlatTreeNode>(this.treeControl, []);

  hasChild = (_: number, node: NxFlatTreeNode) => node.expandable;

  constructor() {
    combineLatest([this.apiService.getGlobtroterMenu(), this.apiService.getGlobtroterUser()])
      .pipe(
        takeUntilDestroyed(),
        map(([menuResult, userResult]) => {
          const navItems = menuResult.result;
          if (navItems) {
            return [
              {
                label: `${userResult.user.firstName} ${userResult.user.lastName}`,
                type: 'menu',
                isSpaLink: false,
                items: [
                  {
                    label: this.translateService.instant('globtroter-menu.news') as string,
                    type: 'link',
                    link: staticGlobtroterPaths.news,
                    isSpaLink: true,
                    items: [],
                  },
                  {
                    label: this.translateService.instant('globtroter-menu.user-profile') as string,
                    type: 'link',
                    link: staticGlobtroterPaths.userProfile,
                    isSpaLink: true,
                    items: [],
                  },
                  {
                    label: this.translateService.instant('globtroter-menu.change-password') as string,
                    type: 'link',
                    link: staticGlobtroterPaths.changePassword,
                    isSpaLink: true,
                    items: [],
                  },
                  {
                    label: this.translateService.instant('globtroter-menu.logout') as string,
                    type: 'link',
                    link: staticGlobtroterPaths.logout,
                    isSpaLink: true,
                    items: [],
                  },
                ],
              },
              ...navItems,
            ] as MenuItem[];
          }
          return undefined;
        }),
        map((navItems) => navItems?.map((item) => this.mapMenuItemToTreeNode(item))),
        tap((nodes) => {
          this.dataSource = new NxTreeFlatDataSource(this.treeControl, nodes);
        }),
      )
      .subscribe();
  }

  private mapMenuItemToTreeNode(menuItem: MenuItem): MyTreeNode {
    return {
      label: menuItem.label,
      link: menuItem.link,
      isSpaLink: menuItem.isSpaLink,
      children: menuItem.items?.map((item) => this.mapMenuItemToTreeNode(item)),
    };
  }
}
